import React, { useState, useEffect, useCallback } from 'react';
import { Employee, EmployeeCreate, EmployeeUpdate } from '../../types/employee';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../../services/api';

interface EmployeeManagementProps {
  bcpId: number;
  onEmployeesChange?: (employees: Employee[]) => void;
}

const EmployeeManagement: React.FC<EmployeeManagementProps> = ({ bcpId, onEmployeesChange }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<EmployeeCreate>({
    bcp_id: bcpId,
    name: '',
    role: '',
    email: '',
    phone: '',
    department: '',
    is_primary_contact: false,
    activation_authority: 'Limited',
    backup_for: '',
    notes: ''
  });

  const roleOptions = [
    'CEO', 'President', 'Operations Manager', 'IT Director', 'Communications Manager',
    'HR Manager', 'Finance Manager', 'Crisis Commander', 'Deputy Manager', 'Team Lead'
  ];

  const authorityOptions = ['Full', 'Limited', 'None'];

  const loadEmployees = useCallback(async () => {
    try {
      const data = await getEmployees(bcpId);
      setEmployees(data);
      onEmployeesChange?.(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [bcpId, onEmployeesChange]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        const updateData: EmployeeUpdate = {
          name: formData.name,
          role: formData.role,
          email: formData.email,
          phone: formData.phone,
          department: formData.department,
          is_primary_contact: formData.is_primary_contact,
          activation_authority: formData.activation_authority,
          backup_for: formData.backup_for,
          notes: formData.notes
        };
        await updateEmployee(editingEmployee.id, updateData as any);
      } else {
        // Check for duplicates before creating
        const isDuplicate = employees.some(emp =>
          emp.name.toLowerCase() === formData.name.toLowerCase() &&
          emp.role === formData.role
        );

        if (isDuplicate) {
          alert(`Employee with name "${formData.name}" and role "${formData.role}" already exists.`);
          return;
        }

        await createEmployee(bcpId, formData as any);
      }

      await loadEmployees();
      resetForm();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving employee:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail || 'Duplicate employee detected');
      } else {
        alert('Error saving employee. Please try again.');
      }
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      bcp_id: bcpId,
      name: employee.name,
      role: employee.role,
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      is_primary_contact: employee.is_primary_contact,
      activation_authority: employee.activation_authority || 'Limited',
      backup_for: employee.backup_for || '',
      notes: employee.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (employeeId: number) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(employeeId);
        await loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      bcp_id: bcpId,
      name: '',
      role: '',
      email: '',
      phone: '',
      department: '',
      is_primary_contact: false,
      activation_authority: 'Limited',
      backup_for: '',
      notes: ''
    });
    setEditingEmployee(null);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Add Team Member
        </button>
      </div>

      {employees.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <p>No team members added yet.</p>
          <p className="text-sm">Add team members to define roles and responsibilities for your BCP.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Authority</th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                        {employee.is_primary_contact && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Primary Contact
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employee.role}</div>
                    {employee.department && (
                      <div className="text-sm text-gray-500">{employee.department}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employee.email || 'No email'}</div>
                    <div className="text-sm text-gray-500">{employee.phone || 'No phone'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      employee.activation_authority === 'Full' ? 'bg-red-100 text-red-800' :
                      employee.activation_authority === 'Limited' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {employee.activation_authority || 'Limited'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                    <button
                      onClick={() => handleEdit(employee)}
                      className="mr-3 text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(employee.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="overflow-y-auto fixed inset-0 z-50 w-full h-full bg-gray-600 bg-opacity-50">
          <div className="relative top-20 p-5 mx-auto w-96 bg-white rounded-md border shadow-lg">
            <div className="mt-3">
              <h3 className="mb-4 text-lg font-medium text-gray-900">
                {editingEmployee ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Role *</label>
                  <select
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a role</option>
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Activation Authority</label>
                    <select
                      value={formData.activation_authority}
                      onChange={(e) => setFormData({ ...formData, activation_authority: e.target.value as 'Full' | 'Limited' | 'None' })}
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {authorityOptions.map(auth => (
                        <option key={auth} value={auth}>{auth}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Backup For</label>
                    <input
                      type="text"
                      value={formData.backup_for}
                      onChange={(e) => setFormData({ ...formData, backup_for: e.target.value })}
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_primary_contact"
                    checked={formData.is_primary_contact}
                    onChange={(e) => setFormData({ ...formData, is_primary_contact: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="is_primary_contact" className="block ml-2 text-sm text-gray-900">
                    Primary Contact
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end pt-4 space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingEmployee ? 'Update' : 'Add'} Team Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
